import React, { useState, useEffect, useContext, useRef } from 'react';
import { 
  Button, TextField, Typography, Box, Container, 
  CssBaseline, Card, Grid, CardContent, 
  CircularProgress, TableContainer, Table, 
  TableHead, TableBody, TableRow, TableCell, 
  CardActions 
} from '@mui/material';
import { 
  doc, getDocs, addDoc, updateDoc, 
  collection, query, where, Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import Layout from "./Layout";
import AppContext from './AppContext';
import { format, isValid, set } from "date-fns";
import ReactToPrint from 'react-to-print';
import EndOfDayReport from './reports/EndofDayReport';
import OverdueOrdersCard from './components/OverdueOrdersCard';

const Shift = () => {
  const { setIsOnShift, isOnShift, userId, shopId, shiftData, updateShiftData, openShift, closeShift, t } = useContext(AppContext);
  const [openingCash, setOpeningCash] = useState(0);
  const [actualCash, setActualCash] = useState(0);
  const [cashBookData, setCashBookData] = useState([]);
  const [cashPayments, setCashPayments] = useState(0);
  const [refunds, setRefunds] = useState(0);
  const [paidIn, setPaidIn] = useState(0);
  const [paidOut, setPaidOut] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showOverdueOrders, setShowOverdueOrders] = useState(false);
  const [showAcceptButton, setShowAcceptButton] = useState(false);
  const [showEndOfDayReport, setShowEndOfDayReport] = useState(false);
  const componentRef = useRef(null);
  const endOfDayReportRef = useRef();
  const [cashError, setCashError] = useState('');
  const [isLoadingOverdue, setIsLoadingOverdue] = useState(false);
  const reactToPrintRef = useRef(null);
  const [isOpeningShift, setIsOpeningShift] = useState(false);

  useEffect(() => {
    const fetchCashBookData = async () => {
      if (isOnShift) {
        setLoading(true);
        try {
          const shiftStartTime = shiftData.shiftStartTime;
          const q = query(
            collection(db, 'cashbook'), 
            where('userId', '==', userId), 
            where('shopId', '==', shopId), 
            where('paymentMethod', '==', 'Cash'), 
            where('timestamp', '>=', shiftStartTime)
          );
          
          const querySnapshot = await getDocs(q);
          const data = querySnapshot.docs.map(doc => doc.data());
          setCashBookData(data);

          const totals = data.reduce((acc, entry) => {
            switch (entry.transactionType) {
              case 'income':
              case 'intermediary':
                acc.cashPayments += entry.amount;
                break;
              case 'refund':
                acc.refunds += entry.amount;
                break;
              case 'paidIn':
                acc.paidIn += entry.amount;
                break;
              case 'paidOut':
                acc.paidOut += entry.amount;
                break;
            }
            return acc;
          }, { cashPayments: 0, refunds: 0, paidIn: 0, paidOut: 0 });

          setCashPayments(totals.cashPayments);
          setRefunds(totals.refunds);
          setPaidIn(totals.paidIn);
          setPaidOut(totals.paidOut);
        
        } catch (error) {
          console.error("Error fetching cashbook data:", error);
          setCashBookData([]);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchCashBookData();
  }, [userId, shopId, shiftData, isOnShift]);

  const handleOpenShift = async () => {
    setIsOpeningShift(true);
    await openShift(openingCash);
    setIsOpeningShift(false);
  };

  const handleCloseShift = async () => {

    const validate=validateCash(actualCash);
    if (!validate) {
      return;
    }
    closeShift(actualCash,cashPayments,refunds,paidIn,paidOut);
    return true;
  };

  // Utility functions remain the same as in the previous implementation
  const calculateTotalAmount = () => 
    cashBookData.reduce((total, entry) => 
      entry.transactionType === "income" ? total + entry.amount : total - entry.amount, 0);

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const validateCash = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setCashError(t('Please enter a valid number'));
      return false;
    }
    if (numValue < 0) {
      setCashError(t('Cash amount cannot be negative'));
      return false;
    }
    if(cashPayments>0 && numValue===0) {
      setCashError(t('Actual cash amount cannot be zero'));
      return false;
    }
    
    setCashError('');
    return true;
  };

  const handleEndDay = () => {
    if (!showOverdueOrders) {
      setIsLoadingOverdue(true);
      // Simulating async loading - replace with your actual data fetch
      setTimeout(() => {
        setShowOverdueOrders(true);
        setShowAcceptButton(true);
        setIsLoadingOverdue(false);
      }, 1000); // Adjust timing or replace with actual API call
    }
  };
  
  const handleAccept = () => {
    setShowAcceptButton(false);
    setShowEndOfDayReport(true);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Layout>
      <Container>
      {isOpeningShift ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      ) : (
          <>
            {!isOnShift ? (
              
              <Box>
                <Typography variant="h6" mt={3}>{t('Open Shift')}</Typography>
                <TextField
                  label={t('Opening Cash Amount')}
                  value={openingCash}
                  onChange={(e) => setOpeningCash(e.target.value)}
                  type="number"
                  sx={{ marginTop: '10px' }}
                />
                <div>
                <Button variant="contained" color="primary" onClick={handleOpenShift} sx={{ marginTop: '10px', display: 'inline' }} disableElevation>
                  {t('Open Shift')}
                </Button>
                <Button variant="contained" color="secondary" onClick={handleEndDay} sx={{marginTop:'10px',marginLeft:'10px'}} disableElevation>{t('End Day')}</Button>
                </div>

               {/* Overdue Orders Section */}
                {isLoadingOverdue ? (
                  <Box sx={{ mt: 2 }}>
                    <Typography>{t('Loading overdue orders...')}</Typography>
                    {/* You could also use a Material-UI CircularProgress component */}
                    {/* <CircularProgress /> */}
                  </Box>
                ) : showOverdueOrders && (
                  <>
                    <OverdueOrdersCard />
                    {showAcceptButton && (
                      <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={handleAccept} 
                        sx={{ mt: 2 }}
                      >
                        {t('Accept and End Day')}
                      </Button>
                    )}
                    {showEndOfDayReport && (
                      <ReactToPrint
                        trigger={() => (
                          <Button variant="outlined" color="secondary" sx={{ mt: 2 }}>
                            {t('Print End of Day Report')}
                          </Button>
                        )}
                        content={() => endOfDayReportRef.current}
                      />
                    )}
                  </>
                )}
                <div style={{ display: 'none' }}>
                <EndOfDayReport ref={endOfDayReportRef} shopId={shopId} date={new Date()} />
                </div>

                </Box>
               
               
            ) : (
              <>
                 <Card sx={{ maxWidth: 600, margin: '0 auto', padding: 0, marginTop: '16px' }} variant='outlined'>
                  <CardContent>
                <ShiftReportCard
                  ref={componentRef}
                  shiftData={shiftData}
                  cashPayments={cashPayments}
                  refunds={refunds}
                  paidIn={paidIn}
                  paidOut={paidOut}
                  formatter={formatter}
                  actualCash={actualCash}
                  t={t}
                />
              
                </CardContent>
                <CardActions>
               
                </CardActions>
                </Card>
                <Card sx={{ maxWidth: 600, margin: '0 auto', padding: 0, marginTop: '16px' }} variant='outlined'>
                  <CardContent>
                  <TextField
                    label={t('Actual Cash Amount')}
                    type="number"
                    value={actualCash}
                    onChange={(e) => {
                      setActualCash(Number(e.target.value));
                      validateCash(e.target.value);
                    }}
                    error={!!cashError}
                    helperText={cashError}
                    fullWidth
                    margin="normal"
                  />
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <ReactToPrint
                    trigger={() => <></>} 
                    content={() => componentRef.current}
                    ref={(el) => (reactToPrintRef.current = el)}
                    />
                     <Button
                        variant="contained"
                        color="secondary"
                        onClick={async () => {
                          const canPrint = await handleCloseShift();
                          //console.log(canPrint);
                        //  if (canPrint) {
                          //  reactToPrintRef.current.handlePrint(); // Trigger the print if handleCloseShift returns true
                        //  }
                        }}
                        disableElevation
                      >
                        {t('Close Shift')}
                      </Button>
                  </CardActions>
                </Card>
                
                {/* Cashbook Data Table */}
            {cashBookData && Array.isArray(cashBookData) && cashBookData?.length > 0 ? (
              <TableContainer sx={{ marginTop: 4 }}>
                <Table aria-label={t("Cashbook data table")}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('Order ID')}</TableCell>
                      <TableCell>{t('Date & Time')}</TableCell>
                      <TableCell>{t('Description')}</TableCell>
                      <TableCell>{t('Payment Method')}</TableCell>
                      <TableCell>{t('In/Out')}</TableCell>
                      <TableCell>{t('Amount')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cashBookData.map((entry, index) => (
                      <TableRow key={index}>
                        <TableCell>{entry.orderId}</TableCell>
                        <TableCell>{format(entry.timestamp.toDate(),"dd-MM-yyyy h:mm a")}</TableCell>
                        <TableCell>{entry.description}</TableCell>
                        <TableCell>{entry.paymentMethod}</TableCell>
                        <TableCell>{entry.transactionType === 'income' || entry.transactionType==='intermediary' ? t('In') : t('Out')}</TableCell>
                        <TableCell>{formatter.format(entry.amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={4} align="right"><strong>{t('Total')}</strong></TableCell>
                      <TableCell><strong>{formatter.format(calculateTotalAmount())} {t("MMK")}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" sx={{ marginTop: 4 }}>
                {t('No shift data available.')}
              </Typography>
            )}
          
              </>
            )}
          </>
        )}
      </Container>
      </Layout>
    </ThemeProvider>
  );
};

// Printable Shift Report Component
const ShiftReportCard = React.forwardRef(({ shiftData, cashPayments, refunds, paidIn, paidOut, formatter, actualCash, t }, ref) => {
  if(shiftData!=null) {
    const expectedCash = parseInt(shiftData.openingCash) + cashPayments - refunds + paidIn - paidOut;

  return (
    <Box ref={ref}>
      <Typography variant="h4" mt={0} align="center" gutterBottom>
        {t('Shift Report')}
      </Typography>
      <Grid container spacing={0} sx={{ padding: 0 }}>
            <Grid item xs={6}>
          <Typography variant="subtitle1">{t('Shift ID')}:</Typography>
        </Grid>
        <Grid item xs={6}>
      <Typography variant="body1">{shiftData.id}</Typography>
      
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">{t('Shift Start Time')}:</Typography>
        </Grid>
        <Grid item xs={6}>
      <Typography variant="body1">{shiftData.shiftStartTime && (shiftData.shiftStartTime instanceof Date
        ? format(shiftData.shiftStartTime, "dd-MM-yyyy h:mm a")
        : shiftData.shiftStartTime.toDate
        ? format(shiftData.shiftStartTime.toDate(), "dd-MM-yyyy h:mm a")
        : t('Invalid date'))}
      </Typography>
      
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">{t('Shift End Time')}:</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1">{format(Timestamp.now().toDate(),"dd-MM-yyyy h:mm a")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">{t('User Name')}:</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1">{Array.isArray(shiftData) ? shiftData[0].userName : shiftData.userName}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">{t('Starting Cash')}:</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1">{Array.isArray(shiftData) ? shiftData[0].openingCash : shiftData.openingCash} {t("MMK")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">{t('Cash Payments')}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1">{formatter.format(cashPayments)} {t("MMK")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">{t('Refunds')}:</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1">{formatter.format(refunds)} {t("MMK")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">{t('Paid In')}:</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1">{formatter.format(paidIn)} {t("MMK")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">{t('Paid Out')}:</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1">{formatter.format(paidOut)} {t("MMK")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">{t('Expected Cash')}:</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1">{formatter.format(expectedCash)} {t("MMK")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">{t('Actual Cash')}:</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1">{formatter.format(actualCash)} {t("MMK")}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">{t('Cash Difference')}:</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1">{formatter.format(actualCash-expectedCash)} {t("MMK")}</Typography>
        </Grid>
      </Grid>
    </Box>
  );
  }
  
});



export default Shift;
